import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { UserCog, Save } from "lucide-react";
import { API, portalHeaders } from "../helpers.js";

export default function ProfileTab({ primaryColor }) {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/portal/profile`, { headers: portalHeaders() });
      setProfile(r.data);
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de charger votre profil");
    }
  };

  const update = (key, val) => {
    setProfile(p => ({ ...p, [key]: val }));
    setDirty(true);
  };

  const validate = () => {
    if (!profile) return {};
    const errs = {};
    if (profile.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) errs.email = "Format invalide";
    if (profile.postal_code && profile.postal_code.trim().length > 0 && profile.postal_code.trim().length < 6) errs.postal_code = "Code postal trop court";
    return errs;
  };

  useEffect(() => { setErrors(validate()); /* live validation */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.email, profile?.postal_code]);

  const save = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error("Verifiez les champs"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/portal/profile`, profile, {
        headers: { ...portalHeaders(), "Content-Type": "application/json" },
      });
      toast.success("Profil sauvegarde");
      setDirty(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  if (!profile) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border p-10 text-center text-gray-400" data-testid="portal-profile-loading">
        <div className="w-5 h-5 border-2 border-gray-200 border-t-gray-500 rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="portal-profile-tab">
      <div className="bg-white rounded-2xl shadow-sm border p-5 space-y-4">
        <h3 className="font-semibold text-sm flex items-center gap-2"><UserCog className="w-4 h-4" /> Mes informations</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Prenom" readonly>
            <input type="text" value={profile.first_name || ""} readOnly
              className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 cursor-not-allowed" />
          </Field>
          <Field label="Nom" readonly>
            <input type="text" value={profile.last_name || ""} readOnly
              className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 cursor-not-allowed" />
          </Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Courriel" error={errors.email}>
            <input type="email" value={profile.email || ""} onChange={e => update("email", e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 text-sm ${errors.email ? "border-red-300 bg-red-50" : ""}`}
              data-testid="profile-email" />
          </Field>
          <Field label="Telephone">
            <input type="tel" value={profile.phone || ""} onChange={e => update("phone", e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="profile-phone" />
          </Field>
        </div>
        <Field label="Adresse">
          <input type="text" value={profile.address || ""} onChange={e => update("address", e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="profile-address" />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Ville">
            <input type="text" value={profile.city || ""} onChange={e => update("city", e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="profile-city" />
          </Field>
          <Field label="Province">
            <input type="text" value={profile.province || ""} onChange={e => update("province", e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="profile-province" />
          </Field>
          <Field label="Code postal" error={errors.postal_code}>
            <input type="text" value={profile.postal_code || ""} onChange={e => update("postal_code", e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 text-sm ${errors.postal_code ? "border-red-300 bg-red-50" : ""}`}
              data-testid="profile-postal-code" />
          </Field>
        </div>
        <p className="text-xs text-gray-400">
          Pour modifier votre nom, veuillez contacter votre cabinet.
        </p>
      </div>
      <button onClick={save} disabled={saving || !dirty || Object.keys(validate()).length > 0}
        className="w-full sm:w-auto text-white px-6 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
        style={{ backgroundColor: primaryColor }} data-testid="portal-global-save-btn">
        <Save className="w-4 h-4" /> {saving ? "Sauvegarde..." : "Sauvegarder"}
      </button>
    </div>
  );
}

function Field({ label, children, error, readonly }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}{readonly && <span className="ml-1 text-gray-400 font-normal">(lecture seule)</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-500 mt-0.5">{error}</p>}
    </div>
  );
}
